import { useState, useRef, useEffect } from "react";
import { useLocation } from "@/lib/simple-router";
import { Calendar, Users, Search, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import stripeLogo from "@assets/Powered by Stripe - white.png";

interface EnhancedBookingWidgetProps {
  className?: string;
  variant?: 'hero' | 'inline';
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Share interaction states, not dimensions, between the two booking triggers.
const bookingTriggerStyles = "hover:border-[var(--toka-primary)] hover:bg-background hover:text-inherit data-[state=open]:border-[var(--toka-primary)] focus-visible:border-[var(--toka-primary)] focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0";

export default function EnhancedBookingWidget({ className = "", variant = 'hero' }: EnhancedBookingWidgetProps) {
  const [location, setLocation] = useLocation();
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState("2");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isSelectingCheckIn, setIsSelectingCheckIn] = useState(true);

  // Initialize from URL parameters when component mounts
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window?.location?.search || '');
      
      const checkInParam = searchParams.get('checkIn');
      const checkOutParam = searchParams.get('checkOut');
      const guestsParam = searchParams.get('guests');
      
      if (checkInParam && typeof checkInParam === 'string' && checkInParam.includes('-')) {
        try {
          // Parse date without timezone conversion to avoid off-by-one errors
          const dateParts = checkInParam.split('-');
          if (dateParts.length === 3) {
            const [year, month, day] = dateParts.map(Number);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
              const checkInDate = new Date(year, month - 1, day);
              setCheckIn(checkInDate);
            }
          }
        } catch (error) {
          console.warn('Error parsing checkIn parameter:', error);
        }
      }
      if (checkOutParam && typeof checkOutParam === 'string' && checkOutParam.includes('-')) {
        try {
          // Parse date without timezone conversion to avoid off-by-one errors
          const dateParts = checkOutParam.split('-');
          if (dateParts.length === 3) {
            const [year, month, day] = dateParts.map(Number);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
              const checkOutDate = new Date(year, month - 1, day);
              setCheckOut(checkOutDate);
            }
          }
        } catch (error) {
          console.warn('Error parsing checkOut parameter:', error);
        }
      }
      if (guestsParam && typeof guestsParam === 'string') {
        setGuests(guestsParam);
      }
    } catch (error) {
      console.warn('Error parsing URL parameters in enhanced booking widget:', error);
    }
  }, [location]);

  const handleSearch = () => {
    console.log('Search button clicked!', { checkIn, checkOut, guests });
    const params = new URLSearchParams();
    if (checkIn) {
      // Format as YYYY-MM-DD using the exact date the user selected (no timezone conversion)
      const dateStr = `${checkIn.getFullYear()}-${String(checkIn.getMonth() + 1).padStart(2, '0')}-${String(checkIn.getDate()).padStart(2, '0')}`;
      params.append('checkIn', dateStr);
    }
    if (checkOut) {
      // Format as YYYY-MM-DD using the exact date the user selected (no timezone conversion)
      const dateStr = `${checkOut.getFullYear()}-${String(checkOut.getMonth() + 1).padStart(2, '0')}-${String(checkOut.getDate()).padStart(2, '0')}`;
      params.append('checkOut', dateStr);
    }
    if (guests) params.append('guests', guests);
    
    const url = `/villas?${params.toString()}`;
    console.log('Navigating to:', url);
    
    // Force navigation even if already on the villas page
    window.location.href = url;
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateInRange = (date: Date) => {
    if (!checkIn || !checkOut) return false;
    return date >= checkIn && date <= checkOut;
  };

  const isDateSelected = (date: Date) => {
    if (checkIn && date.toDateString() === checkIn.toDateString()) return true;
    if (checkOut && date.toDateString() === checkOut.toDateString()) return true;
    return false;
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    if (isSelectingCheckIn) {
      setCheckIn(date);
      setCheckOut(null);
      setIsSelectingCheckIn(false);
    } else {
      if (checkIn && date <= checkIn) {
        setCheckIn(date);
        setCheckOut(null);
        setIsSelectingCheckIn(false);
      } else {
        setCheckOut(date);
        setIsCalendarOpen(false);
        setIsSelectingCheckIn(true);
      }
    }
  };

  const clearDates = () => {
    setCheckIn(null);
    setCheckOut(null);
    setIsSelectingCheckIn(true);
  };

  const applyDates = () => {
    setIsCalendarOpen(false);
    setIsSelectingCheckIn(true);
  };

  const renderMonthCalendar = (month: Date) => {
    const daysInMonth = getDaysInMonth(month);
    const firstDay = getFirstDayOfMonth(month);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const isDisabled = isDateDisabled(date);
      const isSelected = isDateSelected(date);
      const isInRange = isDateInRange(date);

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(date)}
          disabled={isDisabled}
          className={`
            w-8 h-8 text-sm rounded transition-colors
            ${isDisabled 
              ? 'text-toka-text/30 cursor-not-allowed' 
              : isSelected
              ? 'bg-[#AD6433] text-white hover:bg-[#8B4513] cursor-pointer'
              : isInRange
              ? 'bg-[#63452f] text-white hover:bg-[#805233] cursor-pointer'
              : 'text-toka-text hover:bg-[#63452f] cursor-pointer'
            }
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  // Get the next month for two-month display
  const getNextMonth = () => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() + 1);
    return next;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const widgetContent = (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
      {/* Date Selection with Calendar Popup */}
      <div className="md:col-span-2">
        <Label className="block text-sm font-medium text-toka-text mb-2">
          Check in • Check out
        </Label>
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={`w-full h-10 justify-start text-left font-normal ${bookingTriggerStyles}`}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {checkIn && checkOut 
                ? `${formatDate(checkIn)} • ${formatDate(checkOut)}`
                : checkIn 
                ? `${formatDate(checkIn)} • Check out`
                : "Check in • Check out"
              }
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={8} avoidCollisions={false}>
            <div className="bg-background border rounded-lg shadow-lg p-4">
              {/* Two-month layout on desktop, single month on mobile */}
              <div className="flex flex-col md:flex-row md:gap-6">
                {/* First Month */}
                <div className="min-w-[280px]">
                  {/* Calendar Header - First Month */}
                  <div className="flex items-center justify-between mb-4">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => navigateMonth('prev')}
                      className="p-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="font-semibold">
                      {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </div>
                    <div className="w-8 md:hidden">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => navigateMonth('next')}
                        className="p-1"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="w-8 hidden md:block" />
                  </div>

                  {/* Days of Week Header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS.map(day => (
                      <div key={day} className="w-8 h-8 text-xs text-toka-text flex items-center justify-center font-medium">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid - First Month */}
                  <div className="grid grid-cols-7 gap-1 mb-4">
                    {renderMonthCalendar(currentMonth)}
                  </div>
                </div>

                {/* Second Month - Hidden on mobile */}
                <div className="hidden md:block min-w-[280px]">
                  {/* Calendar Header - Second Month */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-8" />
                    <div className="font-semibold">
                      {MONTHS[getNextMonth().getMonth()]} {getNextMonth().getFullYear()}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => navigateMonth('next')}
                      className="p-1"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Days of Week Header */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS.map(day => (
                      <div key={`next-${day}`} className="w-8 h-8 text-xs text-toka-text flex items-center justify-center font-medium">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid - Second Month */}
                  <div className="grid grid-cols-7 gap-1 mb-4">
                    {renderMonthCalendar(getNextMonth())}
                  </div>
                </div>
              </div>

              {/* Calendar Footer */}
              <div className="flex items-center justify-between pt-3 border-t">
                <Button variant="ghost" size="sm" onClick={clearDates}>Clear Dates</Button>
                <Button 
                  size="sm" 
                  onClick={applyDates}
                  className="bg-[#AD6433] hover:bg-[#8B4513] text-white"
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      {/* Guests Selection */}
      <div>
        <Label htmlFor="guests" className="block text-sm font-medium text-toka-text mb-2">
          Guests
        </Label>
        <Select value={guests} onValueChange={setGuests}>
          <SelectTrigger className={`h-10 ${bookingTriggerStyles}`} aria-label="Select number of guests">
            <Users className="mr-2 h-4 w-4" aria-hidden="true" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 Guest</SelectItem>
            <SelectItem value="2">2 Guests</SelectItem>
            <SelectItem value="4">4 Guests</SelectItem>
            <SelectItem value="6">6 Guests</SelectItem>
            <SelectItem value="8">8 Guests</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Search Button */}
      <div>
        <Button 
          onClick={handleSearch}
          className="w-full h-10 bg-[#8B5025] hover:bg-[#7A4520] text-white font-semibold"
          type="button"
        >
          <Search className="mr-2 h-4 w-4" />
          Search
        </Button>
      </div>
    </div>
  );

  if (variant === 'hero') {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto' }} className={className}>
        <Card className="booking-widget" style={{ backgroundColor: 'rgba(47, 48, 49, 0.7)', backdropFilter: 'blur(4px)' }}>
          <CardContent className="p-4">
            {widgetContent}
            <div className="mt-4 flex flex-col items-center justify-center text-sm text-toka-text">
              <span className="text-foreground text-center text-[12px]">Secure Payments</span>
              <div className="flex items-center gap-3 mt-2">
                <img 
                  src={stripeLogo} 
                  alt="Powered by Stripe" 
                  className="h-5 w-auto"
                  width={88}
                  height={20}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`bg-background rounded-lg shadow-sm border border-border p-4 ${className}`}>
      {widgetContent}
      <div className="mt-4 flex flex-col items-center justify-center text-sm text-toka-text">
        <span className="text-foreground text-center text-[12px]">Secure Payments</span>
        <div className="flex items-center gap-3 mt-2">
          <img 
            src={stripeLogo} 
            alt="Powered by Stripe" 
            className="h-5 w-auto"
            width={88}
            height={20}
          />
        </div>
      </div>
    </div>
  );
}